import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  Container,
  Title,
  StyledForm,
  SubmitButton,
  Label,
  Input,
  Textarea,
  SubTitle,
} from "globalComponents/FormComponents";
import { days, getTimezones } from "globalResources/constants";
import axios from "axios";
import { errorHandler } from "globalResources/util";
import { BackButton, Button } from "globalComponents";
import { getTheme } from "Theme";
import {
  clearInviteCode,
  enterCompany,
  getInviteCode,
  saveSession,
} from "./session";

const theme = getTheme();

const Main = styled.div`
  display: flex;
  flex-direction: column;
`;
const ErrorText = styled.p`
  color: ${theme.NEGATIVE};
  margin: 0;
`;
const Hint = styled.p`
  font-size: 0.9em;
  margin: 0;
  opacity: 0.8;
`;
const RequestRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
`;
const Status = styled.span`
  font-weight: 700;
  text-transform: capitalize;
  color: ${(p) =>
    p.status === "accepted"
      ? theme.SUCCESS
      : p.status === "declined"
        ? theme.NEGATIVE
        : "#b26a00"};
`;
const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
`;

const OrDivider = styled.div`
  text-align: center;
  margin: 12px 0 4px 0;
  opacity: 0.7;
  font-size: 0.9em;
`;

const Account = () => {
  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [createForm, setCreateForm] = useState({
    name: "",
    workweekStartDay: "monday",
    accountingTimezone: localTimezone,
    description: "",
  });
  // from an invite's sign-up link (see RootUser.jsx)
  const inviteCode = getInviteCode();
  // code, or accountId when picked from the companies open to requests
  const [joinForm, setJoinForm] = useState({ code: inviteCode, accountId: "", message: "" });
  const [joinable, setJoinable] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [createOrJoin, setcreateOrJoin] = useState(inviteCode ? "join" : "");
  const [requests, setRequests] = useState([]);

  const loadRequests = useCallback(async () => {
    try {
      const res = await axios.get("/account/join-requests");
      setRequests(res.data.requests || []);
    } catch (err) {
      const { message } = await errorHandler(err);
      setError(message);
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  // companies anyone can ask to join, for the join step's picker
  useEffect(() => {
    if (createOrJoin !== "join") return;
    axios
      .get("/account/joinable")
      .then((res) => setJoinable(res.data.companies || []))
      .catch(() => setJoinable([]));
  }, [createOrJoin]);

  const pending = requests.filter((r) => r.status === "pending");

  const createCompany = async () => {
    if (!createForm.name.trim()) return setError("Company name is required");
    setSaving(true);
    setError("");
    try {
      const res = await axios.post("/account", createForm);
      saveSession(res.data.token, res.data.user);
      window.location.replace("/dashboard");
    } catch (err) {
      const { message } = await errorHandler(err);
      setError(message || "Couldn't create the company");
    } finally {
      setSaving(false);
    }
  };

  const requestToJoin = async () => {
    if (!joinForm.accountId && joinForm.code.replace(/[^a-z0-9]/gi, "").length !== 8)
      return setError(
        joinable.length
          ? "Pick your company, or enter the 8-character code from your company admin"
          : "Enter the 8-character code from your company admin",
      );
    setSaving(true);
    setError("");
    try {
      await axios.post(
        "/account/join-request",
        joinForm.accountId
          ? { accountId: joinForm.accountId, message: joinForm.message }
          : { code: joinForm.code, message: joinForm.message },
      );
      clearInviteCode();
      setJoinForm({ code: "", accountId: "", message: "" });
      setcreateOrJoin("");
      await loadRequests();
    } catch (err) {
      const { message } = await errorHandler(err);
      setError(message || "Couldn't send the request");
    } finally {
      setSaving(false);
    }
  };

  const cancelRequest = async (id) => {
    try {
      await axios.patch("/account/join-request/cancel", { id });
      await loadRequests();
    } catch (err) {
      const { message } = await errorHandler(err);
      setError(message);
    }
  };

  // after an admin accepts, the person can go straight in
  const checkAgain = async () => {
    setError("");
    try {
      const { data } = await axios.get("/root/onboarding");
      if (data.hasAccount)
        return enterCompany(data.memberships[data.defaultUser] || data.memberships[0]);
      await loadRequests();
    } catch (err) {
      const { message } = await errorHandler(err);
      setError(message);
    }
  };

  if (createOrJoin === "create") {
    return (
      <Main>
        <Container>
          <Title>Company Account Setup</Title>
          <StyledForm>
            <Label tooltip="Name of your company or just your personal name if only you">
              Company Name
            </Label>
            <Input
              type="text"
              name="companyName"
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
            />
            <Label tooltip="This will be the day automatic reports and billing cycles will start on">
              Workweek Start Day
            </Label>
            <Input
              as="select"
              name="workweekStartDay"
              value={createForm.workweekStartDay}
              onChange={(e) =>
                setCreateForm({ ...createForm, workweekStartDay: e.target.value })
              }
            >
              {days.map((day) => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </Input>
            <Label tooltip="This is your companies operating timezone that everything will be relative to for reporting purposes.">
              Reporting Timezone
            </Label>
            <Input
              as="select"
              name="accountingTimezone"
              value={createForm.accountingTimezone}
              onChange={(e) =>
                setCreateForm({ ...createForm, accountingTimezone: e.target.value })
              }
            >
              {getTimezones().map((timezone) => (
                <option key={timezone.value} value={timezone.value}>
                  {timezone.label}
                </option>
              ))}
            </Input>
            <Label>Company Details</Label>
            <Textarea
              rows={4}
              name="description"
              placeholder="Short description of what your company is and does"
              value={createForm.description}
              onChange={(e) => {
                if (e.target.value.length <= 200)
                  setCreateForm({ ...createForm, description: e.target.value });
              }}
            />
            <Hint>
              We'll set up Owner, Manager and Employee groups and an internal
              client you can log your own time against.
            </Hint>
            {error && <ErrorText>{error}</ErrorText>}
            <BackButton onClick={() => { setError(""); setcreateOrJoin(""); }}>
              Cancel
            </BackButton>
            <SubmitButton onClick={saving ? undefined : createCompany}>
              {saving ? "Creating..." : "Create"}
            </SubmitButton>
          </StyledForm>
        </Container>
      </Main>
    );
  }

  if (createOrJoin === "join") {
    return (
      <Main>
        <Container>
          <Title>Request to Join a Company</Title>
          <StyledForm>
            {joinable.length > 0 && (
              <>
                <Label>Find your company</Label>
                <Input
                  as="select"
                  name="joinCompany"
                  value={joinForm.accountId}
                  onChange={(e) =>
                    setJoinForm({ ...joinForm, accountId: e.target.value, code: "" })
                  }
                >
                  <option value="">Choose a company...</option>
                  {joinable.map((c) => (
                    <option key={c._id} value={c._id} disabled={c.requested}>
                      {c.name}
                      {c.requested ? " (already requested)" : ""}
                    </option>
                  ))}
                </Input>
                <OrDivider>or enter a company code</OrDivider>
              </>
            )}
            <Label tooltip="Admins find it on their Company page">
              Company Code
            </Label>
            <Input
              name="joinCode"
              placeholder="ABCD-EFGH"
              autoComplete="off"
              value={joinForm.code}
              onChange={(e) =>
                setJoinForm({ ...joinForm, code: e.target.value.toUpperCase(), accountId: "" })
              }
            />
            <Label>
              Message <i style={{ fontSize: 12 }}>(optional)</i>
            </Label>
            <Textarea
              rows={3}
              name="joinMessage"
              placeholder="Let the admin know who you are"
              value={joinForm.message}
              onChange={(e) => {
                if (e.target.value.length <= 500)
                  setJoinForm({ ...joinForm, message: e.target.value });
              }}
            />
            {error && <ErrorText>{error}</ErrorText>}
            <Row>
              <Button color="grey" width="50%" onClick={() => { setError(""); setcreateOrJoin(""); }}>
                Back
              </Button>
              <Button width="50%" onClick={saving ? () => {} : requestToJoin}>
                {saving ? "Sending..." : "Send Request"}
              </Button>
            </Row>
          </StyledForm>
        </Container>
      </Main>
    );
  }

  return (
    <Main>
      <Container>
        <Title>Welcome to TCS</Title>
        <SubTitle>
          Request access to an existing company account or create a new one to
          get started using TCS
        </SubTitle>
        {requests.length > 0 && (
          <div data-testid="join-requests">
            <Label>Your requests</Label>
            {requests.slice(0, 5).map((r) => (
              <RequestRow key={r._id} data-testid="join-request">
                <span>{r.account?.name || "Company"}</span>
                <span>
                  <Status status={r.status}>{r.status}</Status>
                  {r.status === "pending" && (
                    <Button color="grey" margin="0 0 0 10px" onClick={() => cancelRequest(r._id)}>
                      Cancel
                    </Button>
                  )}
                </span>
              </RequestRow>
            ))}
            {pending.length > 0 && (
              <Hint style={{ margin: "10px 0" }}>
                An admin needs to accept your request. You'll get an email when
                they do.
              </Hint>
            )}
            <Button margin="0 0 16px 0" onClick={checkAgain}>
              Check Again
            </Button>
          </div>
        )}
        {error && <ErrorText>{error}</ErrorText>}
        <SubmitButton onClick={() => { setError(""); setcreateOrJoin("join"); }}>
          Join a Company
        </SubmitButton>{" "}
        <SubmitButton onClick={() => { setError(""); setcreateOrJoin("create"); }}>
          New Company Account
        </SubmitButton>
      </Container>
    </Main>
  );
};
export default Account;
